import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "bloq_install_dismissed";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

// "Install BLOQ Talk" PWA prompt. Uses the native beforeinstallprompt on Chrome/Android;
// falls back to an "Add to Home Screen" hint on iOS Safari (which never fires the event).
export function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");

  useEffect(() => {
    if (dismissed || isStandalone()) return;
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    if (isIos()) setIosHint(true);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, [dismissed]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
    setShow(false);
    setIosHint(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch {
      /* user dismissed the native sheet */
    }
    setDeferred(null);
    dismiss();
  };

  if (dismissed || isStandalone() || (!show && !iosHint)) return null;

  return (
    <div className="mb-3 flex items-center gap-3 rounded-2xl border border-bloq-mint/25 bg-bloq-mint/[0.07] p-3" data-testid="install-prompt">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-bloq-mint/20 text-bloq-mint">
        <Download className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-white">Install BLOQ Talk</div>
        <div className="truncate text-[11px] text-bloq-muted">
          {iosHint ? 'Tap Share, then "Add to Home Screen"' : "Add it to your home screen for one-tap access"}
        </div>
      </div>
      {show && (
        <button onClick={install} className="shrink-0 rounded-full bg-bloq-mint px-3.5 py-1.5 text-xs font-bold text-bloq-bg transition-opacity hover:opacity-90" data-testid="install-prompt-btn">
          Install
        </button>
      )}
      {iosHint && !show && <Share className="h-4 w-4 shrink-0 text-bloq-mint" />}
      <button onClick={dismiss} className="shrink-0 text-bloq-muted transition-colors hover:text-white" title="Dismiss" data-testid="install-prompt-dismiss">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
